import { useState, useContext, useRef, useEffect } from "react";
import { AuthContext } from "../../auth/AuthContext";
import api from "../../api/client";

// ── OTP digit input ───────────────────────────────────────────────────────────
function OtpInput({ value, onChange, disabled }) {
  const refs   = useRef([]);
  const digits = value.split("").concat(Array(6).fill("")).slice(0, 6);

  useEffect(() => {
    const first = digits.findIndex(d => !d);
    refs.current[first === -1 ? 5 : first]?.focus();
  }, []);

  const [focusIdx, setFocusIdx] = useState(null);

  const handleChange = (idx, e) => {
    const ch = e.target.value.replace(/\D/g, "").slice(-1);
    if (!ch) return;
    const next = [...digits];
    next[idx]  = ch;
    onChange(next.join("").slice(0, 6));
    if (idx < 5) refs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const next = [...digits];
      if (next[idx]) { next[idx] = ""; onChange(next.join("")); }
      else if (idx > 0) { next[idx-1] = ""; onChange(next.join("")); refs.current[idx-1]?.focus(); }
    } else if (e.key === "ArrowLeft"  && idx > 0) refs.current[idx-1]?.focus();
    else if   (e.key === "ArrowRight" && idx < 5) refs.current[idx+1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const p = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (p) { onChange(p); refs.current[Math.min(p.length, 5)]?.focus(); }
  };

  return (
    <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={el => refs.current[i] = el}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          disabled={disabled}
          onChange={e => handleChange(i, e)}
          onKeyDown={e => handleKeyDown(i, e)}
          onPaste={handlePaste}
          onFocus={() => setFocusIdx(i)}
          onBlur={() => setFocusIdx(null)}
          style={{
            width: 44, height: 52,
            border: `2px solid ${focusIdx === i ? "#1a6fa8" : d ? "#0d9488" : "#e2eaf3"}`,
            borderRadius: 10,
            textAlign: "center",
            fontSize: 20,
            fontWeight: 700,
            fontFamily: "'DM Sans', sans-serif",
            color: "#0f2944",
            background: d ? "#f0fdf9" : focusIdx === i ? "#fff" : "#f8fafc",
            outline: "none",
            boxShadow: focusIdx === i ? "0 0 0 3px #e8f4fd" : "none",
            transition: "all 0.15s",
            caretColor: "transparent",
          }}
        />
      ))}
    </div>
  );
}

// ── Main modal ────────────────────────────────────────────────────────────────
export default function PhoneVerifyModal() {
  const { needsPhoneVerification, markPhoneVerified } = useContext(AuthContext);

  const [step,    setStep]    = useState("phone");  // "phone" | "otp"
  const [phone,   setPhone]   = useState("");
  const [otp,     setOtp]     = useState("");
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [counter, setCounter] = useState(0);
  const [focused, setFocused] = useState(false);

  // Countdown timer
  useEffect(() => {
    if (counter <= 0) return;
    const t = setTimeout(() => setCounter(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [counter]);

  // Auto-submit OTP when 6 digits entered
  useEffect(() => {
    if (otp.length === 6) confirmOtp(otp);
  }, [otp]);

  if (!needsPhoneVerification) return null;

  const sendOtp = async (e) => {
    e?.preventDefault();
    setError("");
    const digits = phone.replace(/\D/g, "");
    if (digits.length !== 9) {
      setError("Please enter a valid 9-digit number");
      return;
    }
    const localPhone = "0" + digits;
    setLoading(true);
    try {
      await api.post("auth/google/verify-phone/", { step: "send", phone: localPhone });
      setStep("otp");
      setCounter(60);
      setOtp("");
    } catch (err) {
      setError(err.response?.data?.error ?? "Could not send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const confirmOtp = async (code = otp) => {
    if (code.length !== 6) return;
    setError("");
    setLoading(true);
    try {
      const digits     = phone.replace(/\D/g, "");
      const localPhone = "0" + digits;
      const res = await api.post("auth/google/verify-phone/", {
        step:  "confirm",
        phone: localPhone,
        otp:   code,
      });
      // Pass new token if account was merged with existing phone account
      markPhoneVerified(res.data.token || null);
    } catch (err) {
      setError(err.response?.data?.error ?? "Invalid OTP. Please try again.");
      setOtp("");
    } finally {
      setLoading(false);
    }
  };

  const displayPhone = ("0" + phone.replace(/\D/g, ""))
    .replace(/^(0\d{2})(\d{3})(\d{4})$/, "$1 $2 $3");

  return (
    <>
      {/* Backdrop — non-dismissible: no onClick */}
      <div style={{
        position: "fixed", inset: 0, zIndex: 999,
        background: "rgba(15,41,68,0.55)",
        backdropFilter: "blur(3px)",
      }} />

      {/* Modal */}
      <div style={{
        position: "fixed", inset: 0, zIndex: 1000,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "20px 16px",
      }}>
        <div style={{
          background: "white",
          borderRadius: 20,
          padding: "36px 32px",
          width: "100%",
          maxWidth: 420,
          boxShadow: "0 24px 60px rgba(15,41,68,0.25)",
          fontFamily: "'DM Sans', sans-serif",
        }}>

          {/* Logo + heading */}
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 14,
              background: "linear-gradient(135deg, #1a6fa8, #0d9488)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 16px",
            }}>
              <svg viewBox="0 0 64 64" width={22} height={22}>
                <rect x="0"  y="10" width="12" height="44" rx="3" fill="white"/>
                <rect x="52" y="10" width="12" height="44" rx="3" fill="white"/>
                <polyline points="0,33 14,33 19,18 24,46 29,28 34,38 39,33 64,33"
                  fill="none" stroke="white" strokeWidth="4.5"
                  strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>

            <h2 style={{
              fontFamily: "'Lora', Georgia, serif",
              fontSize: 18, fontWeight: 600,
              color: "#0f2944", margin: "0 0 8px",
            }}>
              {step === "phone" ? "Verify your mobile number" : "Enter verification code"}
            </h2>

            <p style={{ fontSize: 13, color: "#4a6080", margin: 0, lineHeight: 1.6 }}>
              {step === "phone"
                ? "To connect with your pharmacy, we need to verify your mobile number. This is a one-time step."
                : `We sent a 6-digit code to ${displayPhone}`
              }
            </p>
          </div>

          {/* Info strip */}
          {step === "phone" && (
            <div style={{
              background: "#eff6ff", border: "1px solid #bfdbfe",
              borderRadius: 10, padding: "10px 14px",
              fontSize: 12.5, color: "#1e40af", marginBottom: 20,
              display: "flex", gap: 8, alignItems: "flex-start",
            }}>
              <i className="pi pi-info-circle" style={{ fontSize: 13, flexShrink: 0, marginTop: 1 }} />
              This should be the same number registered at your pharmacy.
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{
              background: "#fef2f2", border: "1px solid #fca5a5",
              borderRadius: 10, padding: "10px 14px",
              fontSize: 13, color: "#b91c1c", marginBottom: 16,
              display: "flex", gap: 8, alignItems: "center",
            }}>
              <i className="pi pi-exclamation-circle" style={{ fontSize: 13, flexShrink: 0 }} />
              {error}
            </div>
          )}

          {/* ── Phone step ── */}
          {step === "phone" && (
            <form onSubmit={sendOtp}>
              <label style={{
                display: "block", fontSize: 11.5, fontWeight: 600,
                color: "#4a6080", letterSpacing: "0.4px",
                textTransform: "uppercase", marginBottom: 7,
              }}>
                Mobile Number
              </label>
              <div style={{
                display: "flex",
                border: `1.5px solid ${focused ? "#1a6fa8" : "#e2eaf3"}`,
                borderRadius: 10, overflow: "hidden",
                boxShadow: focused ? "0 0 0 3px #e8f4fd" : "none",
                transition: "all 0.15s", marginBottom: 20,
              }}>
                <div style={{
                  padding: "11px 14px", background: "#f0f5fa",
                  fontSize: 14, fontWeight: 600, color: "#4a6080",
                  borderRight: "1px solid #e2eaf3", whiteSpace: "nowrap",
                  display: "flex", alignItems: "center",
                }}>
                  🇱🇰 +94
                </div>
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={9}
                  value={phone}
                  onChange={e => setPhone(e.target.value.replace(/\D/g, "").replace(/^0+/, "").slice(0, 9))}
                  placeholder="7X XXX XXXX"
                  autoFocus
                  style={{
                    flex: 1, border: "none", outline: "none",
                    padding: "11px 14px", fontSize: 15,
                    fontFamily: "'DM Sans', sans-serif",
                    color: "#0f2944", background: "white",
                    letterSpacing: "1px",
                  }}
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                />
              </div>
              <button
                type="submit"
                disabled={loading || phone.replace(/\D/g, "").length !== 9}
                style={{
                  width: "100%", padding: "13px", border: "none",
                  borderRadius: 10,
                  background: "linear-gradient(135deg, #1a6fa8, #0d9488)",
                  color: "white", fontSize: 14, fontWeight: 600,
                  fontFamily: "'DM Sans', sans-serif", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  opacity: (loading || phone.replace(/\D/g, "").length !== 9) ? 0.65 : 1,
                  transition: "opacity 0.15s",
                }}
              >
                {loading
                  ? <><i className="pi pi-spin pi-spinner" style={{ fontSize: 14 }} /> Sending…</>
                  : <><i className="pi pi-send"   style={{ fontSize: 14 }} /> Send Verification Code</>
                }
              </button>
            </form>
          )}

          {/* ── OTP step ── */}
          {step === "otp" && (
            <div>
              <OtpInput value={otp} onChange={setOtp} disabled={loading} />

              {otp.length > 0 && otp.length < 6 && (
                <div style={{ textAlign: "center", fontSize: 12, color: "#8fa3bc", marginTop: 10 }}>
                  {6 - otp.length} digit{6 - otp.length !== 1 ? "s" : ""} remaining
                </div>
              )}

              <button
                onClick={() => confirmOtp()}
                disabled={loading || otp.length !== 6}
                style={{
                  width: "100%", padding: "13px", border: "none",
                  borderRadius: 10, marginTop: 20,
                  background: "linear-gradient(135deg, #1a6fa8, #0d9488)",
                  color: "white", fontSize: 14, fontWeight: 600,
                  fontFamily: "'DM Sans', sans-serif", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  opacity: (loading || otp.length !== 6) ? 0.65 : 1,
                  transition: "opacity 0.15s",
                }}
              >
                {loading
                  ? <><i className="pi pi-spin pi-spinner" style={{ fontSize: 14 }} /> Verifying…</>
                  : <><i className="pi pi-shield"          style={{ fontSize: 14 }} /> Verify Number</>
                }
              </button>

              {/* Resend + change number */}
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16 }}>
                {counter > 0 ? (
                  <span style={{ fontSize: 12.5, color: "#8fa3bc" }}>
                    Resend in <b style={{ color: "#4a6080" }}>{counter}s</b>
                  </span>
                ) : (
                  <button onClick={sendOtp} style={{
                    background: "none", border: "none", cursor: "pointer",
                    fontSize: 12.5, fontWeight: 600, color: "#1a6fa8",
                    fontFamily: "'DM Sans', sans-serif",
                    display: "flex", alignItems: "center", gap: 5, padding: 0,
                  }}>
                    <i className="pi pi-refresh" style={{ fontSize: 11 }} /> Resend code
                  </button>
                )}
                <button onClick={() => { setStep("phone"); setOtp(""); setError(""); }} style={{
                  background: "none", border: "none", cursor: "pointer",
                  fontSize: 12.5, color: "#4a6080",
                  fontFamily: "'DM Sans', sans-serif", padding: 0,
                }}>
                  Change number
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
}